/**
 * VerticalNav Component
 * 
 * Manages step-by-step navigation for the video creation workflow.
 * Handles dependency checking for steps that require previous data (audio/video).
 * Shows modals when dependencies are missing and guides users to required steps.
 * 
 * Dependencies:
 * - Content Creation requires audio (finalAudio or originalAudioUrl)
 * - Caption Creation requires video (generatedVideoUrl or originalVideoUrl)
 * - Sound Generation requires video (generatedVideoUrl or originalVideoUrl)
 * - Title & Description requires video (generatedVideoUrl or originalVideoUrl)
 * - Social Media Export requires video (generatedVideoUrl or originalVideoUrl)
 * 
 * @author AI Assistant
 * @version 2.0 - Refactored with dependency checking and better organization
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './VerticalNav.module.css';
import { StepIndicatorNames } from '../../types';
import DependencyModal from '../Modal/DependencyModal';
import { useCaptionModal } from '../../context/CaptionModalContext';
import { useTitleGeneration } from '../../context/TitleGenerationContext';
import { useNotification } from '../../context/NotificationContext';
import { fetchCaptionsFromS3 } from '../VideoEditor/captionService';
import { useThumbnailModal } from '../../hooks/useThumbnailModal';
import { ThumbnailModal } from '../TitleDescriptionPage/components';
import { uploadExtractedFrameImage } from '../../services/thumbnailGenerationApi';
import VideoFrameSelectionModal from '../Modal/VideoFrameSelectionModal';

/**
 * Props for VerticalNav component
 */
interface VerticalNavProps {
  /** Function called when user navigates to a step (delegates to parent) */
  handleNavSelected: (key: StepIndicatorNames) => void;
  
  /** Initial step to highlight when component loads */
  initialStep?: StepIndicatorNames;
  
  /** Project data containing audio/video URLs for dependency checking */
  projectData?: {
    /** Original uploaded audio file URL */
    originalAudioUrl?: string;
    /** Processed/final audio file URL (takes priority over original) */
    finalAudio?: string;
    /** Original uploaded video file URL */
    originalVideoUrl?: string;
    /** AI-generated video file URL (takes priority over original) */
    generatedVideoUrl?: string;
    /** Project thumbnail URL */
    thumbnailUrl?: string;
    /** Alternative thumbnail image property */
    thumbnail?: string;
    /** Project title for modal */
    title?: string;
    /** Captioned video URL */
    captionedVideoUrl?: string;
    /** Whether project already has captions */
    hasCaptions?: boolean;
    /** Title generation data - if exists, title step is available */
    titleData?: any;
    /** Script generation data - if exists, script step is available */
    scriptData?: any;
    /** Any other project properties */
    [key: string]: any;
  } | null;
}

const VerticalNav:React.FC<VerticalNavProps> = ({handleNavSelected, initialStep, projectData}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('verticalNav');
  const { openCaptionModal } = useCaptionModal();
  const { openTitleModal } = useTitleGeneration();
  const { showNotification } = useNotification();
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Modal state
  const [dependencyModal, setDependencyModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    primaryButton: { text: '', onClick: null },
    secondaryButton: null
  });

  // Video frame selection state
  const [isVideoFrameModalOpen, setIsVideoFrameModalOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  // console.log("=============>", projectData)
  
  // Get numeric step ID based on initialStep enum or default to Caption Creation (5)
  const getInitialStepId = () => {
    if (initialStep === StepIndicatorNames.ScriptGeneration) return 1;
    if (initialStep === StepIndicatorNames.IdeaCreation) return 2;
    if (initialStep === StepIndicatorNames.AudioGeneration) return 3;
    if (initialStep === StepIndicatorNames.ContentCreation) return 4;
    if (initialStep === StepIndicatorNames.CaptionCreation) return 5;
    if (initialStep === StepIndicatorNames.SoundGeneration) return 6;
    if (initialStep === StepIndicatorNames.TitleDescTag) return 7;
    if (initialStep === StepIndicatorNames.ThumbnailCreation) return 8;
    if (initialStep === StepIndicatorNames.Export) return 9;
    return 5; // Default to Caption Creation
  };
  
  const [currentStep, setCurrentStep] = useState(getInitialStepId()); 
  
  // Set completed steps based on currentStep (all previous steps are completed)
  const getCompletedSteps = (step: number): number[] => {
    const completed: number[] = [];
    for (let i = 1; i < step; i++) {
      completed.push(i);
    }
    return completed;
  };
  
  const [completedSteps, setCompletedSteps] = useState<number[]>(getCompletedSteps(getInitialStepId()));
  
  // Automatically set the appropriate step based on the URL
  useEffect(() => {
    // Only apply this logic if initialStep isn't provided
    //if (initialStep) return;
    
    const searchParams = new URLSearchParams(location.search);
    
    // Check if we're on the create-content page with audio URL or project ID
    if (location.pathname.includes('/create-content') && 
        (searchParams.has('audio-url') || searchParams.has('project-id'))) {
      // Set current step to Content Creation (4)
      setCurrentStep(4);
      setCompletedSteps([1, 2, 3] as number[]);
    }
    
    // Check if we're on the caption editor page
    if (location.pathname.includes('/caption') && 
        searchParams.has('project-id')) {
      // Set current step to Caption Creation (5)
      setCurrentStep(5);
      setCompletedSteps([1, 2, 3, 4] as number[]);
    }

    // Check if we're on the title description page
    if (location.pathname.includes('/title-description/')) {
      // Set current step to Title & Description (7)
      setCurrentStep(7);
      setCompletedSteps([1, 2, 3, 4, 5, 6] as number[]);
    }
  }, [location, initialStep]);

  
  const steps = [
    { id: 1, name: t('steps.scriptGeneration'), icon: '📜', key: StepIndicatorNames.ScriptGeneration },
    { id: 2, name: t('steps.promptCreation'), icon: '💡', key: StepIndicatorNames.IdeaCreation },
    { id: 3, name: t('steps.audioGeneration'), icon: '🎵', key: StepIndicatorNames.AudioGeneration },
    { id: 4, name: t('steps.contentCreation'), icon: '📝', key: StepIndicatorNames.ContentCreation },
    { id: 5, name: t('steps.captionCreation'), icon: '✏️', key: StepIndicatorNames.CaptionCreation },
    { id: 6, name: t('steps.soundGeneration'), icon: '🎶', key: StepIndicatorNames.SoundGeneration },
    { id: 7, name: t('steps.titleDescTag'), icon: '🏷️', key: StepIndicatorNames.TitleDescTag },
    { id: 8, name: t('steps.thumbnailCreation'), icon: '🖼️', key: StepIndicatorNames.ThumbnailCreation },
    { id: 9, name: t('steps.export'), icon: '📤', key: StepIndicatorNames.Export }
  ];
  
  // Handle mouse movement to show nav when cursor is near left edge
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientX <= 10 && !isVisible) {
        setIsVisible(true);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);
  
  const hideNav = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to container
    setIsVisible(false);
  };
  
  // We're no longer setting these handlers on the container
  const handleNavMouseEnter = () => {
    setIsExpanded(true);
  };
  
  const handleNavMouseLeave = () => {
    setIsExpanded(false);
  };
  
  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Extracts project ID from current URL parameters or path
   * @returns {string|null} Project ID or null if not found
   */
  const getCurrentProjectId = () => {
    // console.log('🔍 [VerticalNav] getCurrentProjectId called');
    const searchParams = new URLSearchParams(location.search);
    const projectIdFromQuery = searchParams.get('project-id');
    
    if (projectIdFromQuery) {
      return projectIdFromQuery;
    }
    
    const pathSegments = location.pathname.split('/');
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const isUUID = uuidPattern.test(segment);
      const isNumeric = !isNaN(Number(segment)) && segment !== '' && segment.length > 3;
      
      if (segment && (isUUID || isNumeric)) {
        return segment;
      }
    }
    return null;
  };
  
  /**
   * Gets the best available audio URL from project data
   * @returns {object|null} { url: string, source: 'final'|'original' } or null
   */
  const getAvailableAudioUrl = () => {
    if (projectData?.finalAudio) {
      console.log('🔍 [VerticalNav] getAvailableAudioUrl found finalAudio:', projectData.finalAudio);
      return { url: projectData.finalAudio, source: 'final' };
    }
    if (projectData?.originalAudioUrl) {
      console.log('🔍 [VerticalNav] getAvailableAudioUrl found originalAudioUrl:', projectData.originalAudioUrl);
      return { url: projectData.originalAudioUrl, source: 'original' };
    }
    return null;
  };
  
  /**
   * Gets the best available video URL from project data
   * @returns {string|null} Video URL or null if not found
   */
  const getAvailableVideoUrl = () => {
    console.log('🔍 [VerticalNav] getAvailableVideoUrl called', projectData);
    return projectData?.generatedVideoUrl || projectData?.originalVideoUrl || projectData?.captionedVideoUrl|| projectData?.soundTrackVideoUrl || null;
  };
  
  /**
   * Creates and displays a dependency modal with given configuration
   * @param {object} config - Modal configuration
   */
  const showDependencyModal = (config) => {
    try {
      setDependencyModal({
        isOpen: true,
        ...config
      });
    } catch (error) {
      console.error('Error showing dependency modal:', error);
    }
  };
  
  // ============================================================================
  // THUMBNAIL MODAL SETUP
  // ============================================================================
  
  // Handle video frame selection for thumbnail
  const handleVideoFrameSelection = () => {
    console.log('🎬 [VerticalNav] Opening video frame modal with URL:', projectData?.generatedVideoUrl || projectData?.generatedVideoUrl || projectData?.captionedVideoUrl);
    
    const projectId = getCurrentProjectId();
    if (projectId && (projectData?.generatedVideoUrl || projectData?.originalVideoUrl || projectData?.captionedVideoUrl)) {
      // Convert the project video URL to a virtual file for the modal
      const virtualVideoFile = {
        name: `Project Video.mp4`,
        type: 'video/mp4',
        size: 0,
        isProjectVideo: true,
        projectId: projectId,
        projectTitle: `Project Video`,
        s3Url: projectData.generatedVideoUrl || projectData.originalVideoUrl || projectData.captionedVideoUrl,
        lastModified: Date.now(),
        webkitRelativePath: '',
        arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
        slice: () => new Blob(),
        stream: () => new ReadableStream(),
        text: () => Promise.resolve('')
      };
      
      console.log('📦 [VerticalNav] Created virtual video file:', virtualVideoFile);
      
      setSelectedVideoFile(virtualVideoFile as any);
      setIsVideoFrameModalOpen(true);
      // Keep the thumbnail modal open in the background
    } else {
      console.log('⚠️ [VerticalNav] No video available for frame selection');
    }
  };

  // Handle frame selection from video extractor
  const handleFrameSelected = async (imageData: string, timestamp: number) => {
    console.log('🎯 [VerticalNav] Frame selected handler called:', { timestamp, imageDataLength: imageData.length });
    
    // Close the video frame modal first
    setIsVideoFrameModalOpen(false);
    setSelectedVideoFile(null);
    
    try {
      console.log('⏳ [VerticalNav] Starting frame upload process...');
      
      const projectId = getCurrentProjectId();
      const projectTitle = projectData?.title || 'Untitled Project';
      
      // Upload the extracted frame image using the existing project
      const result = await uploadExtractedFrameImage(imageData, {
        projectId: projectId,
        title: `${projectTitle} - Video Frame at ${Math.floor(timestamp / 60)}:${Math.floor(timestamp % 60).toString().padStart(2, '0')}`,
        timestamp: timestamp
      });
      
      console.log('📤 [VerticalNav] Upload result:', result);
      
      if (result) {
        showNotification({
          title: t('notifications.frameSelected'),
          message: t('notifications.frameSelectedMessage'),
          type: 'success'
        });
        
        thumbnailHandlers.closeModal();
        
        console.log('🚀 [VerticalNav] Navigating to thumbnail editor...');
        // Navigate to thumbnail editor with the extracted frame
        navigate(`/thumbnail-editor/${projectId}?baseImage=${encodeURIComponent(result.s3Url)}`);
      }
    } catch (error) {
      console.error('❌ [VerticalNav] Frame selection error:', error);
      showNotification({
        title: t('notifications.error'),
        message: error instanceof Error ? error.message : t('notifications.frameProcessError'),
        type: 'error'
      });
    }
  };

  // Initialize thumbnail modal hook
  const { modalState: thumbnailModalState, handlers: thumbnailHandlers } = useThumbnailModal(
    getCurrentProjectId() || '', 
    projectData?.title || 'Untitled Project',
    handleVideoFrameSelection,
    projectData?.thumbnail, // Only check thumbnail property (matching ScriptLayout pattern)
    {
      original: projectData?.originalVideoUrl,
      generated: projectData?.generatedVideoUrl,
      captioned: projectData?.captionedVideoUrl
    }
  );
  
  // Debug logging to see what thumbnail data VerticalNav receives
  // console.log('🔍 [VERTICAL_NAV] Debug thumbnail data:', {
  //   'projectData?.thumbnail': projectData?.thumbnail,
  //   'projectData exists': !!projectData,
  //   'projectData keys': projectData ? Object.keys(projectData) : 'none',
  //   'full projectData': projectData ? JSON.stringify(projectData, null, 2) : 'null'
  // });
  
  // ============================================================================
  // NAVIGATION HANDLERS WITH DEPENDENCY CHECKING
  // ============================================================================
  
  /**
   * Handles navigation to Audio Generation
   * Enhanced Logic: 
   * 1. If has audio URLs: Direct redirect to audio editor
   * 2. If no audio: Show "Audio Required" dependency modal
   */
  const navigateToAudioGeneration = () => {
    const projectId = getCurrentProjectId();
    
    if (!projectId) {
      navigate('/audio-generation');
      return;
    }
    
    const audioData = getAvailableAudioUrl();
    const videoData = getAvailableVideoUrl();
    console.log('🎵 [VerticalNav] Available audio data:', audioData);
    console.log("initialStep   ", initialStep)
    if (audioData) { // audioData || !videoData deosnt work because if im in promopt, i dont have audio, also i dont have video, this will be run. 
      // Audio available - proceed to audio editor with the audio URL
         navigate(`/audio-editor/${projectId}?audioUrl=${encodeURIComponent(audioData.url)}`);
    }  else if ( initialStep === StepIndicatorNames.ScriptGeneration || initialStep === StepIndicatorNames.IdeaCreation) {
         // Audio available - proceed to audio editor with the audio URL
        navigate(`/audio-editor/${projectId}`);
    } else if ( projectData?.originalVideoUrl ) {
      console.log("projectData?.originalVideoUrl   ", projectData?.originalVideoUrl)
      const modalConfig = {
        title: t('modals.newProjectAudioRequired.title'),
        message: t('modals.newProjectAudioRequired.message'),
        primaryButton: {
          text: t('modals.newProjectAudioRequired.button'),
          onClick: () => {
            navigate('/audio-generation');
          }
        },
        secondaryButton: null
      };
      showDependencyModal(modalConfig);

    } 
    else {
      // Audio missing - show modal to guide user to audio generation
      const modalConfig = {
        title: t('modals.audioRequired.title'),
        message: t('modals.audioRequired.message'),
        primaryButton: {
          text: t('modals.audioRequired.button'),
          onClick: () => {
            navigate('/audio-generation');
          }
        },
        secondaryButton: null
      };
      
      showDependencyModal(modalConfig);
    }
  };

  /**
   * Handles navigation to Content Creation (Video Editor)
   * Requires: Audio URL (finalAudio or originalAudioUrl)
   * Shows modal if audio is missing, redirects to audio generation
   */
  const navigateToContentCreation = () => {
    console.log('🎯 [VerticalNav] navigateToContentCreation called');
    
    const projectId = getCurrentProjectId();
    console.log('🆔 [VerticalNav] Current project ID:', projectId);
    
    if (!projectId) {
      console.log('❌ [VerticalNav] No project ID found, returning early');
      return;
    }
    
    const audioData = getAvailableAudioUrl();
    console.log('🎵 [VerticalNav] Available audio data:', audioData);
    console.log('📊 [VerticalNav] Project data:', projectData);
    const videoUrl = getAvailableVideoUrl();
    
    if (audioData) {
      // Audio available - proceed to content creation with audio context
      console.log('✅ [VerticalNav] Audio found, navigating to create-content with audio URL');
      const queryParams = new URLSearchParams({
        'project-id': projectId,
        'audio-url': audioData.url
      });
      navigate(`/create-content?${queryParams.toString()}`);
    } else if (videoUrl) {
      // Audio available - proceed to content creation with audio context
      console.log('✅ [VerticalNav] video found, navigating to create-content with videoUrls URL');
      const queryParams = new URLSearchParams({
        'project-id': projectId,
        'from_app': 'ai-video-editor'
      });
      navigate(`/create-content?${queryParams.toString()}`);
    } else {
      // Audio missing - show modal to guide user to audio generation
      console.log('⚠️ [VerticalNav] No audio found, showing dependency modal');
      
      const modalConfig = {
        title: t('modals.audioRequired.title'),
        message: t('modals.audioRequired.message'),
        primaryButton: {
          text: t('modals.audioRequired.button'),
          onClick: () => {
            console.log('🔄 [VerticalNav] Modal primary button clicked, navigating to audio editor');
            navigate(`/audio-editor/${projectId}`);
          }
        },
        secondaryButton: null
      };
      
      console.log('📝 [VerticalNav] Modal config:', modalConfig);
      showDependencyModal(modalConfig);
      console.log('🚀 [VerticalNav] showDependencyModal called');
    }
  };
  
  /**
   * Handles navigation to Caption Creation
   * Enhanced Logic:
   * 1. If has video + has captions: Direct redirect to caption editor
   * 2. If has video + no captions: Show caption generation modal
   * 3. If no video + has audio: Show "Video Required" dependency modal
   * 4. If no video + no audio: Show "Audio Required" dependency modal
   */
  const navigateToCaptionCreation = async () => {
    const projectId = getCurrentProjectId();
    if (!projectId) return;
    
    const videoUrl = getAvailableVideoUrl();
    console.log('🎬 [VerticalNav] Available video URL:', videoUrl);
    
    if (videoUrl) {
      // ✅ NEW LOGIC: Check hasCaptions FIRST - Direct redirect if captions exist
      if (projectData?.hasCaptions) {
        console.log('🎬 [VerticalNav] Project has existing captions, redirecting directly to caption editor');
        console.log('🎬 [VerticalNav] Project data:', { hasCaptions: projectData.hasCaptions, videoUrl });
        const queryParams = new URLSearchParams({
          'project-id': projectId,
          'video-url': videoUrl
        });
        navigate(`/caption?${queryParams.toString()}`);
        return;
      }
      
      console.log('📝 [VerticalNav] No captions detected, showing caption generation modal');
      console.log('📝 [VerticalNav] Project data:', { hasCaptions: projectData?.hasCaptions, videoUrl });
      
      // ✅ EXISTING LOGIC: No captions yet - show modal for generation
      try {
        const existingCaptions = await fetchCaptionsFromS3(projectId);
        
        openCaptionModal({
          projectId: projectId,
          videoUrl: videoUrl,
          existingCaptions: existingCaptions || []
        });
      } catch (error) {
        // No existing captions found, open modal anyway
        openCaptionModal({
          projectId: projectId,
          videoUrl: videoUrl,
          existingCaptions: []
        });
      }
      return;
    }
    
    // No video - check for audio as fallback
    const audioData = getAvailableAudioUrl();
    
    if (audioData) {
      // Has audio but no video - guide to Content Creation
      showDependencyModal({
        title: t('modals.videoRequired.title'),
        message: t('modals.videoRequired.messageCaption'),
        primaryButton: {
          text: t('modals.videoRequired.button'),
          onClick: () => {
            const queryParams = new URLSearchParams({
              'project-id': projectId,
              'audio-url': audioData.url
            });
            navigate(`/create-content?${queryParams.toString()}`);
          }
        },
        secondaryButton: null
      });
    } else {
      // No video AND no audio - guide to Audio Generation first
      showDependencyModal({
        title: t('modals.audioVideoRequired.title'),
        message: t('modals.audioVideoRequired.messageExport'),
        primaryButton: {
          text: t('modals.audioVideoRequired.button'),
          onClick: () => navigate(`/audio-editor/${projectId}`)
        },
        secondaryButton: null
      });
    }
  };
  
  /**
   * Handles navigation to Sound Generation
   * Requires: Video URL (generatedVideoUrl or originalVideoUrl)
   * Fallback: If no video but has audio, guides to Content Creation
   * Final fallback: If no audio either, guides to Audio Generation
   */
  const navigateToSoundGeneration = () => {
    const projectId = getCurrentProjectId();
    if (!projectId) return;
    
    const videoUrl = getAvailableVideoUrl();
    
    if (videoUrl) {
      // Video available - proceed to sound editor
      navigate(`/sound-editor/${projectId}?video-url=${encodeURIComponent(videoUrl)}`);
      return;
    }
    
    // No video - check for audio as fallback
    const audioData = getAvailableAudioUrl();
    
    if (audioData) {
      // Has audio but no video - guide to Content Creation
      showDependencyModal({
        title: t('modals.videoRequired.title'),
        message: t('modals.videoRequired.messageSoundEffect'),
        primaryButton: {
          text: t('modals.videoRequired.button'),
          onClick: () => {
            const queryParams = new URLSearchParams({
              'project-id': projectId,
              'audio-url': audioData.url
            });
            navigate(`/create-content?${queryParams.toString()}`);
          }
        },
        secondaryButton: null
      });
    } else {
      // No video AND no audio - guide to Audio Generation first
      showDependencyModal({
        title: t('modals.audioVideoRequired.title'),
        message: t('modals.audioVideoRequired.messageExport'),
        primaryButton: {
          text: t('modals.audioVideoRequired.button'),
          onClick: () => navigate(`/audio-editor/${projectId}`)
        },
        secondaryButton: null
      });
    }
  };
  
  /**
   * Handles navigation to Title & Description
   * Checks if titleData exists with generated titles, if so navigate directly, otherwise show modal
   */
  const navigateToTitleDescription = () => {
    const projectId = getCurrentProjectId();
    if (!projectId) return;
    
    console.log('🔍 [VerticalNav] Project data:', projectData);
    console.log('🔍 [VerticalNav] titleData check:', projectData?.titleData);
    console.log('🔍 [VerticalNav] titleData is Array:', Array.isArray(projectData?.titleData));
    console.log('🔍 [VerticalNav] titleData length:', projectData?.titleData?.length);
    
    // Check if title data already exists in the project 
    // titleData is an array, so check if array exists and has content
    if (projectData?.titleData && Array.isArray(projectData.titleData) && projectData.titleData.length > 0) {
      // Check if the titleData has generated titles
      const firstTitleData = projectData.titleData[0];
      console.log('🔍 [VerticalNav] First titleData object:', firstTitleData);
      
      if (firstTitleData && firstTitleData.generatedTitles && firstTitleData.generatedTitles.length > 0) {
        // Title data with generated titles exists - navigate directly to title & description page
        console.log('✅ [VerticalNav] Title data with generated titles found, navigating to title & description page');
        navigate(`/title-description/${projectId}`);
        return;
      }
    }
    
    // No title data - check for video to generate titles
    const videoUrl = getAvailableVideoUrl();
    
    if (videoUrl) {
      // Video available - open title generation modal
      console.log('✅ [VerticalNav] Video found, opening title generation modal');
      openTitleModal('video', videoUrl, projectId, projectData?.titleData);
      return;
    }
    
    // No video - check for audio as fallback
    const audioData = getAvailableAudioUrl();
    
    if (audioData) {
      // Has audio but no video - guide to Content Creation
      showDependencyModal({
        title: t('modals.videoRequired.title'),
        message: t('modals.videoRequired.messageTitle'),
        primaryButton: {
          text: t('modals.videoRequired.button'),
          onClick: () => {
            const queryParams = new URLSearchParams({
              'project-id': projectId,
              'audio-url': audioData.url
            });
            navigate(`/create-content?${queryParams.toString()}`);
          }
        },
        secondaryButton: null
      });
    } else {
      // No video AND no audio - guide to Audio Generation first
      showDependencyModal({
        title: t('modals.audioVideoRequired.title'),
        message: t('modals.audioVideoRequired.messageExport'),
        primaryButton: {
          text: t('modals.audioVideoRequired.button'),
          onClick: () => navigate(`/audio-editor/${projectId}`)
        },
        secondaryButton: null
      });
    }
  };
  
  /**
   * Handles navigation to Social Media Export
   * Requires: Video URL (generatedVideoUrl or originalVideoUrl)
   * Fallback: If no video but has audio, guides to Content Creation
   * Final fallback: If no audio either, guides to Audio Generation
   */
  const navigateToSocialMedia = () => {
    const projectId = getCurrentProjectId();
    if (!projectId) return;
    
    const videoUrl = getAvailableVideoUrl();
    console.log("======> videoUrl   ",videoUrl);
    if (videoUrl) {
      // Video available - proceed to social media export
      const queryParams = new URLSearchParams({
        'project-id': projectId,
        'video-url': videoUrl
      });
      navigate(`/social-media?${queryParams.toString()}`);
      return;
    }
    
    // No video - check for audio as fallback
    const audioData = getAvailableAudioUrl();
    
    if (audioData) {
      // Has audio but no video - guide to Content Creation
      showDependencyModal({
        title: t('modals.videoRequired.title'),
        message: t('modals.videoRequired.messageExport'),
        primaryButton: {
          text: t('modals.videoRequired.button'),
          onClick: () => {
            const queryParams = new URLSearchParams({
              'project-id': projectId,
              'audio-url': audioData.url
            });
            navigate(`/create-content?${queryParams.toString()}`);
          }
        },
        secondaryButton: null
      });
    } else {
      // No video AND no audio - guide to Audio Generation first
      showDependencyModal({
        title: t('modals.audioVideoRequired.title'),
        message: t('modals.audioVideoRequired.messageExport'),
        primaryButton: {
          text: t('modals.audioVideoRequired.button'),
          onClick: () => navigate(`/audio-editor/${projectId}`)
        },
        secondaryButton: null
      });
    }
  };
  
  /**
   * Handles navigation to Thumbnail Creation
   * Opens thumbnail modal for VerticalNav, delegates to parent for apps with their own modal
   */
  const navigateToThumbnail = () => {
    console.log('🖼️ [VerticalNav] navigateToThumbnail called');
    
    const projectId = getCurrentProjectId();
    console.log('🆔 [VerticalNav] Project ID for thumbnail:', projectId);
    
    // All apps now use VerticalNav's thumbnail modal consistently
    if (projectId) {
      console.log('✅ [VerticalNav] Opening VerticalNav thumbnail modal');
      thumbnailHandlers.openModal();
    } else {
      console.log('⚠️ [VerticalNav] No project ID, navigating to thumbnail landing');
      navigate('/thumbnail-landing');
    }
  };
  
  // ============================================================================
  // MAIN NAVIGATION HANDLER
  // ============================================================================
  
  /**
   * Main navigation handler for step clicks
   * Routes to appropriate navigation function based on step type
   * @param {number} stepId - Numeric step ID for UI state management  
   * @param {StepIndicatorNames} key - Step enum for navigation logic
   */
  const handleStepClick = (stepId: number, key: StepIndicatorNames) => {
    console.log('🎯 [VerticalNav] handleStepClick called with:', { stepId, key });
    
    // Route to specialized navigation handlers for steps with dependencies
    switch (key) {
      case StepIndicatorNames.AudioGeneration:
        navigateToAudioGeneration();
        return;
        
      case StepIndicatorNames.ContentCreation:
        navigateToContentCreation();
        return;
        
      case StepIndicatorNames.CaptionCreation:
        console.log('📝 [VerticalNav] Caption Creation clicked');
        console.log('🔍 [VerticalNav] Current location:', location.pathname);
        
        // Check if we're already on the title-description page
        if (location.pathname.includes('/title-description')) {
          console.log('✅ [VerticalNav] On title page, delegating caption logic to parent');
          handleNavSelected(key);
          return;
        }
        
        // Not on title page, run standard caption navigation
        navigateToCaptionCreation();
        return;
        
      case StepIndicatorNames.SoundGeneration:
        console.log('🎶 [VerticalNav] Sound Generation clicked');
        navigateToSoundGeneration();
        return;
        
      case StepIndicatorNames.TitleDescTag:
        console.log('🏷️ [VerticalNav] Title Description clicked');
        console.log('🔍 [VerticalNav] Current location:', location.pathname);
        
        // Check if we're already on the title-description page
        if (location.pathname.includes('/title-description')) {
          console.log('✅ [VerticalNav] Already on title page, delegating to parent');
          handleNavSelected(key);
          return;
        }
        
        // Not on title page, run dependency checking
        navigateToTitleDescription();
        return;
        
      case StepIndicatorNames.ThumbnailCreation:
        console.log('🖼️ [VerticalNav] Thumbnail Creation clicked');
        navigateToThumbnail();
        return;
        
      case StepIndicatorNames.Export:
        console.log('📤 [VerticalNav] Export clicked');
        navigateToSocialMedia();
        return;
        
      default:
        // For all other steps (including ThumbnailCreation), use standard navigation
        // This allows parent components like ScriptLayout to handle thumbnail with their own modal
        handleStandardNavigation(stepId, key);
    }
  };
  
  /**
   * Standard navigation handler for steps without dependencies
   * Updates UI state and delegates to parent navigation function
   * @param {number} stepId - Step ID for UI state management
   * @param {StepIndicatorNames} key - Step enum for navigation
   */
  const handleStandardNavigation = (stepId: number, key: StepIndicatorNames) => {
    // Update UI state: mark previous steps as completed
    const completedSteps: number[] = [];
    for (let i = 1; i < stepId; i++) {
      completedSteps.push(i);
    }
    setCompletedSteps(completedSteps);
    setCurrentStep(stepId);
    
    // Update project stage if navigating to Caption Creation
    const projectId = getCurrentProjectId();
    if (projectId && key === StepIndicatorNames.CaptionCreation) {
      updateProjectStage(projectId);
    }
    
    // Delegate to parent component's navigation handler
    handleNavSelected(key);
  };
  
  /**
   * Updates project stage in database when navigating to Caption Creation
   * @param {string} projectId - Project identifier
   */
  const updateProjectStage = async (projectId: string) => {
    try {
      const { updateProjectStage, ProjectStage } = await import('../../services/projectService');
      await updateProjectStage(projectId, ProjectStage.CAPTION);
      console.log("Project stage updated to CAPTION");
    } catch (error) {
      console.error("Failed to update project stage:", error);
    }
  };
  
  const isStepDisabled = (stepId: number, key: StepIndicatorNames) => {
    // No steps are disabled - let the modal handle audio requirements
    return false;
  };
  
  const getStepStatus = (stepId:number, key: StepIndicatorNames) => {
    if (isStepDisabled(stepId, key)) return 'disabled';
    if (completedSteps.includes(stepId)) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'pending';
  };
  
  return (
    <div
      className={`
        ${styles.verticalNavContainer}
        ${isExpanded ? styles.expanded : ''}
        ${!isVisible ? styles.hidden : ''}
      `}
      // Remove the mouse handlers from the container
    >
      
      
        <button
          className={styles.hideButton}
          onClick={hideNav}
          // The button should not trigger the expand effect
        >
          ←
        </button>
      
      
      <nav 
        className={styles.verticalNav}
        // Add the mouse handlers to the nav element instead
        onMouseEnter={handleNavMouseEnter}
        onMouseLeave={handleNavMouseLeave}
      >
        {steps.map((step, index) => {
          const stepStatus = getStepStatus(step.id, step.key);
          const disabled = stepStatus === 'disabled';
          
          return (
            <div
              key={step.id}
              className={`${styles.navItem} ${styles[stepStatus]}`}
              onClick={() => handleStepClick(step.id, step.key)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.stepIndicator}>
                {stepStatus === 'completed' ? (
                  <span className={styles.checkIcon}>✓</span>
                ) : (
                  <span>{step.icon}</span>
                )}
              </div>
              <span className={styles.stepName}>
                {step.name}
              </span>
              
              {/* Vertical connector line (not for the last item) */}
              {index < steps.length - 1 && <div className={styles.verticalLine}></div>}
            </div>
          );
        })}
      </nav>
      
      <DependencyModal
        isOpen={dependencyModal.isOpen}
        onClose={() => setDependencyModal(prev => ({ ...prev, isOpen: false }))}
        title={dependencyModal.title}
        message={dependencyModal.message}
        primaryButton={dependencyModal.primaryButton}
        secondaryButton={dependencyModal.secondaryButton}
      />
      
      {/* Render ThumbnailModal using portal for apps that don't have their own */}
      {thumbnailModalState.isOpen && createPortal(
        <ThumbnailModal
          isOpen={thumbnailModalState.isOpen}
          onClose={thumbnailHandlers.closeModal}
          projectId={getCurrentProjectId() || ''}
          projectStage="VERTICAL_NAV"
          selectedMethod={thumbnailModalState.selectedMethod as 'ai_image' | 'upload' | 'extract' | 'existing_caption' | 'existing' | 'library'}
          onMethodChange={thumbnailHandlers.setMethod}
          description={thumbnailModalState.description}
          onDescriptionChange={thumbnailHandlers.setDescription}
          isGenerating={thumbnailModalState.isGenerating}
          onGenerate={thumbnailHandlers.handleGeneration}
          onImageUpload={thumbnailHandlers.handleImageUpload}
          onFrameSelected={thumbnailHandlers.handleVideoFrameSelection}
          originalVideoUrl={projectData?.originalVideoUrl}
          availableMethods={thumbnailModalState.availableMethods}
          onExistingThumbnail={thumbnailHandlers.handleExistingThumbnail}
        />,
        document.body
      )}

      {/* Video Frame Selection Modal - rendered at top level to avoid conflicts */}
      {isVideoFrameModalOpen && createPortal(
        <VideoFrameSelectionModal
          isOpen={isVideoFrameModalOpen}
          onClose={() => {
            console.log('❌ [VerticalNav] Video frame modal closed without selection');
            setIsVideoFrameModalOpen(false);
            setSelectedVideoFile(null);
          }}
          onFrameSelected={handleFrameSelected}
          videoFile={selectedVideoFile}
          maxFileSize={2 * 1024 * 1024 * 1024} // 2GB
        />,
        document.body
      )}
    </div>
  );
};

export default VerticalNav;